import { useStore } from '@/infra/store';
import { eventBus } from '@/infra/event-bus/EventBus';
import { logger } from '@/infra/logger/Logger';
import { uuid } from '@/infra/util/uuid';
import type { BiomeId } from '@/domain/world/types';
import type { AfkDispatch, AfkDuration, AfkReward } from '@/domain/world/afk';
import { AFK_DURATIONS_MS, computeRewards, isReady } from '@/domain/world/afk';
import { ALL_ITEM_TYPES } from '@/domain/world/types';
import type { ItemType } from '@/domain/world/types';

export interface DispatchInput {
  npcId: string;
  biome: BiomeId;
  duration: AfkDuration;
}

export function dispatch(input: DispatchInput): AfkDispatch | null {
  const s = useStore.getState();
  // Não permite dispatch se já tiver ativo p/ esse NPC
  const existing = (s.world.afkDispatches ?? []).find(
    (d) => d.npcId === input.npcId && d.status !== 'claimed',
  );
  if (existing) {
    logger.warn('afk.dispatch.npc-busy', { npcId: input.npcId });
    return null;
  }
  const npc = s.npcs.find((n) => n.id === input.npcId);
  if (!npc || npc.dead) {
    logger.warn('afk.dispatch.invalid-npc', { npcId: input.npcId });
    return null;
  }
  const d: AfkDispatch = {
    id: `afk-${uuid()}`,
    npcId: input.npcId,
    biome: input.biome,
    startedAtMs: Date.now(),
    durationMs: AFK_DURATIONS_MS[input.duration],
    status: 'active',
  };
  s.addAfkDispatch(d);
  eventBus.emit('afk.dispatched', { dispatchId: d.id, npcId: input.npcId });
  logger.info('afk.dispatch.created', { id: d.id, biome: input.biome, duration: input.duration });
  return d;
}

export function claim(dispatchId: string): AfkReward | null {
  const s = useStore.getState();
  const d = (s.world.afkDispatches ?? []).find((x) => x.id === dispatchId);
  if (!d) return null;
  if (d.status !== 'claimable' && !isReady(d, Date.now())) return null;
  // Calcula rewards se ainda não tem
  let rewards = d.rewards;
  if (!rewards) {
    const npc = s.npcs.find((n) => n.id === d.npcId);
    rewards = computeRewards({
      biome: d.biome,
      durationMs: d.durationMs,
      npcType: npc?.genome.primaryTypes[0] ?? null,
    });
  }
  // Aplica items ao inventory
  for (const k of Object.keys(rewards.items) as ItemType[]) {
    const amount = rewards.items[k] ?? 0;
    if (amount > 0) s.addToInventory(k, amount);
  }
  s.updateAfkDispatch(dispatchId, { status: 'claimed', rewards });
  eventBus.emit('afk.claimed', { dispatchId });
  logger.info('afk.dispatch.claimed', { id: dispatchId, items: rewards.items, xp: rewards.xp });
  return rewards;
}

export function listClaimable(): AfkDispatch[] {
  const s = useStore.getState();
  const now = Date.now();
  return (s.world.afkDispatches ?? []).filter(
    (d) => d.status === 'claimable' || (d.status === 'active' && isReady(d, now)),
  );
}

export function listDispatchableNpcs(): string[] {
  const s = useStore.getState();
  const busy = new Set(
    (s.world.afkDispatches ?? [])
      .filter((d) => d.status !== 'claimed')
      .map((d) => d.npcId),
  );
  return s.npcs.filter((n) => !n.dead && !busy.has(n.id)).map((n) => n.id);
}

// Re-export types convenient
export type { AfkDispatch, AfkDuration, AfkReward };
export { AFK_DURATIONS_MS };
export const AFK_ITEM_TYPES = ALL_ITEM_TYPES;
